/**
 * Route guard for the alert create / build / edit pages.
 *
 * Two independent conditions:
 *   1. the legacy permission `manage_own_alerts` — without it the user goes to
 *      their profile, as before;
 *   2. the paid entitlement `tool.alerts.use` — without it every `/v1/alerts`
 *      call would 403, so the user lands on the alerts page, which renders the
 *      locked teaser with a link to the subscriptions page (not a 403 page and
 *      not the global upgrade modal).
 *
 * Stores are resolved inside the guard (navigation time = Pinia active), same
 * as `entitlementGuard.js`.
 */
import { ACCESS_KEYS } from '@/config/entitlementKeys';
import { useAuthStore } from '@/stores/useAuthStore';
import { useEntitlementsStore } from '@/stores/useEntitlementsStore';

export const ALERTS_TEASER_ROUTE = 'my-environment.alerts';

export async function alertsAccessGuard(to) {
  if (!useAuthStore().hasPermission('manage_own_alerts')) return { name: 'profile' };

  const entitlements = useEntitlementsStore();
  // Never rejects: a failed fetch resolves null with `accessError` set, and
  // `can()` then decides on the access set / JWT claims already held.
  await entitlements.refreshAccessIfStale();
  if (entitlements.can(ACCESS_KEYS.TOOL_ALERTS)) return true;

  // Not granted — or not determinable (access outage): the alerts page sorts
  // that out. It shows the teaser when locked and attempts its normal load when
  // access is unavailable. `entry` rides along for the funnel analytics.
  const entry = to?.query?.entry;
  return { name: ALERTS_TEASER_ROUTE, ...(entry ? { query: { entry } } : {}) };
}
